import { SizeChartColumn } from "./fetch-size-chart"

export type MeasurementStep = {
  key: string
  title: string
  instruction: string
}

/**
 * How-to-measure copy keyed by the size-chart column key. The Fit Intelligence
 * page derives its "How to Measure" steps from whichever measurement columns
 * the selected category's chart actually has — so the guidance changes with the
 * category instead of being hardcoded. Add a key here when a new measurement
 * column is introduced in apps/backend/src/modules/size-chart/templates.ts.
 */
const KNOWN_STEPS: Record<string, Omit<MeasurementStep, "key">> = {
  half_chest: {
    title: "Half Chest",
    instruction:
      "Lay the garment flat and buttoned, then measure straight across the chest from one underarm seam to the other. That single edge-to-edge span is the half chest.",
  },
  chest: {
    title: "Chest",
    instruction:
      "Measure around the fullest part of your chest, keeping the tape level and parallel to the floor with one finger of breathing room.",
  },
  body_length: {
    title: "Length",
    instruction:
      "Measure from the highest point of the shoulder straight down to where you want the hem to finish.",
  },
  length: {
    title: "Length",
    instruction:
      "Measure from the highest point of the shoulder straight down to where you want the hem to finish.",
  },
  shoulder: {
    title: "Shoulder",
    instruction:
      "Measure straight across the back from the edge of one shoulder seam to the edge of the other.",
  },
  sleeve: {
    title: "Sleeve",
    instruction:
      "Measure from the shoulder seam, along the outside of a slightly bent arm, down to the wrist bone.",
  },
  waist: {
    title: "Waist",
    instruction:
      "Measure around your natural waistline where the garment sits, keeping the tape snug but not tight.",
  },
  trouser_waist: {
    title: "Trouser Waist",
    instruction:
      "Measure around the waistband line where the trouser actually sits on you, keeping the tape comfortably level.",
  },
  hip: {
    title: "Hip",
    instruction:
      "Measure around the fullest part of the seat, keeping the tape horizontal all the way around.",
  },
  inseam: {
    title: "Inseam",
    instruction:
      "Measure along the inside leg from the crotch seam down to the bottom of the hem.",
  },
}

function titleFromKey(key: string, label: string): string {
  if (label && label.trim()) return label.trim()
  return key
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ")
}

/**
 * Build the ordered list of measurement steps for a chart from its measurement
 * columns. Unknown columns still get a sensible generic instruction so a new
 * measurement type never renders an empty card.
 */
export function buildMeasurementSteps(
  columns: SizeChartColumn[] | undefined | null
): MeasurementStep[] {
  const seen = new Set<string>()
  const steps: MeasurementStep[] = []

  for (const col of columns || []) {
    if (!col || col.type !== "measurement") continue
    const key = String(col.key || "").toLowerCase()
    if (!key || seen.has(key)) continue
    seen.add(key)

    const known = KNOWN_STEPS[key]
    if (known) {
      steps.push({ key, ...known })
    } else {
      const title = titleFromKey(key, col.label)
      steps.push({
        key,
        title,
        instruction: `Measure the ${title.toLowerCase()} following the garment's natural line, keeping the tape level and snug.`,
      })
    }
  }

  return steps
}
